/**
 * Step 5 — Account (PR-ONB-RESUME-1B, locks #1/#2/#3).
 *
 * The wizard stays anonymous until here (org → contatti → indirizzi →
 * amministratore). This step creates the PROVISIONAL account:
 *
 *   createUserWithEmailAndPassword (Firebase Web SDK, client-side only)
 *   → sendEmailVerification (best-effort; submit gate arrives in PRECHECK-3)
 *   → getIdToken
 *   → POST /area/register (authenticated bind, Turnstile-gated)
 *   → account + DRAFT + server-side autosave from here on.
 *
 * The password lives in LOCAL component state only: it goes straight to the
 * Firebase SDK and never enters the form, the draft payload, our API calls
 * or analytics.
 *
 * Recovery (lock #2): `auth/email-already-in-use` (previous signup whose
 * bind never completed, or an existing account) switches the card to
 * "Accedi per continuare" — login with the SAME email, then the IDEMPOTENT
 * bind resumes/creates the DRAFT. No account re-creation, no automatic
 * password reset (the "Password dimenticata?" link sends the standard
 * Firebase reset email on explicit user action).
 */

import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Turnstile } from '@marsidev/react-turnstile';
import { Banner } from '@ab-platform/ui/banner';
import { FieldError } from '@ab-platform/ui/field-error';
import { FloatingLabelInput } from '@ab-platform/ui/floating-label-input';
import { FocusedPageShell } from '@ab-platform/ui/focused-page-shell';
import { PillButton } from '@ab-platform/ui/pill-button';
import { env } from '@/lib/env';
import { ACCOUNT_CREATED_BANNER } from '@/lib/onboardingUxViews';
import { useAreaSession } from '@/hooks/AreaSessionContext';
import { useWizardSessionIdContext } from '@/hooks/WizardSessionIdContext';
import type { RegistrationFormValues } from '@/hooks/useRegistrationForm';
import { registerAreaBind } from '@/lib/areaApi';
import {
  bindFailureMessage,
  buildDraftPayloadFromForm,
  draftStateAfterCompletedStep,
  mapFirebaseLoginError,
  mapFirebaseSignupError,
} from '@/lib/accountBindFlow';
import {
  buildEmailVerificationBanner,
  sendOutcomeFromAttempt,
} from '@/lib/emailVerificationView';

type Mode = 'signup' | 'login' | 'done';

export function AccountPage(): JSX.Element {
  const navigate = useNavigate();
  const form = useFormContext<RegistrationFormValues>();
  const session = useAreaSession();
  const wizardSessionId = useWizardSessionIdContext();

  const email = form.getValues('adminUser.email');

  const [mode, setMode] = useState<Mode>(session.bind ? 'done' : 'signup');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  // Bumping the key remounts the widget after a consumed/expired token.
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    email?: string;
    password?: string;
  }>({});
  const [resetSent, setResetSent] = useState(false);
  // null = not attempted yet, true = Firebase accepted the send, false = the
  // send was rejected (e.g. sendOobCode 400). Drives the "done" banner copy so
  // we never claim the verification email was sent when it wasn't.
  const [verificationSent, setVerificationSent] = useState<boolean | null>(null);

  const passwordIssue = useMemo((): string | null => {
    if (password.length === 0) return null;
    if (password.length < 8) return 'La password deve avere almeno 8 caratteri.';
    if (passwordConfirm.length > 0 && password !== passwordConfirm) {
      return 'Le password non coincidono.';
    }
    return null;
  }, [password, passwordConfirm]);

  const resetTurnstile = (): void => {
    setTurnstileToken('');
    setTurnstileKey((k) => k + 1);
  };

  const bind = async (idToken: string): Promise<boolean> => {
    const result = await registerAreaBind(idToken, {
      turnstileToken,
      draftPayload: buildDraftPayloadFromForm(
        form.getValues() as unknown as Record<string, unknown>,
      ),
      // The bind succeeding IS the explicit completion of this step
      // (validation + click) — the only place AfterCompletedStep is legal.
      draftState: draftStateAfterCompletedStep('account'),
      ...(wizardSessionId && { wizardSessionId }),
    });
    if (result.ok) {
      session.markBound({
        requestPublicId: result.data.requestPublicId,
        draftRevision: result.data.draftRevision,
      });
      setMode('done');
      return true;
    }
    setFormError(bindFailureMessage(result));
    if (result.kind === 'TURNSTILE_INVALID') resetTurnstile();
    return false;
  };

  const handleSignup = async (): Promise<void> => {
    setFormError(null);
    setFieldErrors({});
    if (!email) {
      setFormError('Email mancante: torna allo step Referente e inseriscila.');
      return;
    }
    if (password.length < 8 || password !== passwordConfirm) {
      setFieldErrors({ password: passwordIssue ?? 'Password non valida.' });
      return;
    }
    if (!turnstileToken) {
      setFormError('Completa la verifica anti-abuso per continuare.');
      return;
    }
    setBusy(true);
    try {
      const fb = await import('@/lib/firebaseClient');
      const auth = fb.getFirebaseAuth();
      let user;
      try {
        const credential = await fb.createUserWithEmailAndPassword(
          auth,
          email,
          password,
        );
        user = credential.user;
      } catch (err) {
        const mapped = mapFirebaseSignupError(
          (err as { code?: string }).code ?? '',
        );
        if (mapped.action === 'SWITCH_TO_LOGIN') {
          setMode('login');
          setFormError(mapped.message);
        } else if (mapped.action === 'FIELD_ERROR') {
          setFieldErrors({ [mapped.field]: mapped.message });
        } else {
          setFormError(mapped.message);
        }
        return;
      }
      // Best-effort: the verification link arrives now; the gate on it is
      // the PRECHECK-3 submit, not the compilation (lock #3). The area's
      // status page offers re-send + re-check CTAs if this delivery fails.
      try {
        await fb.sendVerificationEmail(user);
        setVerificationSent(true);
      } catch {
        // Non-blocking by design — but DON'T claim it was sent. The "done"
        // banner reflects this; the area's status page offers an honest re-send.
        setVerificationSent(false);
      }
      const idToken = await user.getIdToken();
      await bind(idToken);
    } finally {
      setBusy(false);
    }
  };

  const handleLogin = async (): Promise<void> => {
    setFormError(null);
    setFieldErrors({});
    if (!turnstileToken) {
      setFormError('Completa la verifica anti-abuso per continuare.');
      return;
    }
    setBusy(true);
    try {
      const fb = await import('@/lib/firebaseClient');
      let user;
      try {
        const credential = await fb.signInWithEmailAndPassword(
          fb.getFirebaseAuth(),
          email,
          password,
        );
        user = credential.user;
      } catch (err) {
        setFormError(
          mapFirebaseLoginError((err as { code?: string }).code ?? ''),
        );
        return;
      }
      const idToken = await user.getIdToken();
      // Idempotent bind: resumes the existing account/DRAFT or creates the
      // missing one (signup-ok-bind-failed recovery). The values typed in
      // THIS wizard run stay in the form; the next autosave persists them.
      await bind(idToken);
    } finally {
      setBusy(false);
    }
  };

  const handlePasswordReset = async (): Promise<void> => {
    setFormError(null);
    try {
      const fb = await import('@/lib/firebaseClient');
      await fb.sendPasswordResetEmail(fb.getFirebaseAuth(), email);
      setResetSent(true);
    } catch {
      setFormError('Invio email di reset non riuscito. Riprova.');
    }
  };

  if (mode === 'done') {
    // Honesty rule: only claim the verification email was sent when Firebase
    // accepted it (verificationSent === true). A rejected send (sendOobCode 400)
    // or an unknown state (reached via resume) must NOT assert "inviato".
    const verificationBanner = buildEmailVerificationBanner({
      emailVerified: session.user?.emailVerified ?? false,
      sendOutcome: sendOutcomeFromAttempt(verificationSent),
    });
    return (
      <FocusedPageShell
        title="Account creato"
        description="Da questo momento la tua richiesta viene salvata automaticamente: puoi uscire e riprenderla quando vuoi accedendo con email e password."
        onBack={() => navigate('/registrazione-intermediario/amministratore')}
        footer={
          <PillButton
            type="button"
            size="lg"
            className="w-full"
            onClick={() => navigate('/registrazione-intermediario/fiscale')}
          >
            Continua
          </PillButton>
        }
      >
        <div className="grid gap-3">
          <Banner tone="success" title={ACCOUNT_CREATED_BANNER.title}>
            {ACCOUNT_CREATED_BANNER.message}
          </Banner>
          {session.user && verificationBanner.show ? (
            <Banner tone={verificationBanner.tone} title={verificationBanner.title}>
              {verificationBanner.message}
            </Banner>
          ) : null}
        </div>
      </FocusedPageShell>
    );
  }

  const isLogin = mode === 'login';

  return (
    <FocusedPageShell
      title={isLogin ? 'Accedi per continuare' : 'Crea il tuo account'}
      description={
        isLogin
          ? 'Esiste già un account con questa email: accedi con la tua password per riprendere la richiesta.'
          : 'Con email e password potrai salvare la richiesta e riprenderla in qualsiasi momento. La password non viene mai inviata ai nostri sistemi: la gestisce direttamente il provider di autenticazione.'
      }
      onBack={() => navigate('/registrazione-intermediario/amministratore')}
      footer={
        <PillButton
          type="button"
          size="lg"
          className="w-full"
          disabled={busy}
          onClick={() => void (isLogin ? handleLogin() : handleSignup())}
        >
          {busy
            ? 'Attendi…'
            : isLogin
              ? 'Accedi e riprendi la richiesta'
              : 'Crea account e salva la richiesta'}
        </PillButton>
      }
    >
      <div className="grid gap-4">
        <FloatingLabelInput
          label="Email di accesso"
          value={email}
          readOnly
          helperText="È l'email del referente indicata allo step precedente."
          error={fieldErrors.email}
        />
        <FloatingLabelInput
          label="Password"
          type="password"
          autoComplete={isLogin ? 'current-password' : 'new-password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={fieldErrors.password}
          helperText={isLogin ? undefined : 'Almeno 8 caratteri.'}
        />
        {!isLogin ? (
          <FloatingLabelInput
            label="Conferma password"
            type="password"
            autoComplete="new-password"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            error={passwordIssue ?? undefined}
          />
        ) : (
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <PillButton
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => void handlePasswordReset()}
            >
              Password dimenticata?
            </PillButton>
            {resetSent ? <span>Email di reset inviata.</span> : null}
          </div>
        )}

        <div className="grid gap-2">
          <Turnstile
            key={turnstileKey}
            siteKey={env.VITE_TURNSTILE_SITE_KEY}
            onSuccess={(token) => setTurnstileToken(token)}
            onError={() => setTurnstileToken('')}
            onExpire={() => setTurnstileToken('')}
            options={{ theme: 'light' }}
          />
        </div>

        {formError ? <FieldError message={formError} /> : null}

        {isLogin ? (
          <div>
            <PillButton
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setMode('signup');
                setFormError(null);
              }}
            >
              Torna alla creazione account
            </PillButton>
          </div>
        ) : null}
      </div>
    </FocusedPageShell>
  );
}
